import type { BarProcessingSetting, GridValue, MidiNote, MidiTimeSignature, TrackRole } from "./music";
import { createTrackWithClip, type OverdubMonitorSource, type PlaybackMode, type ProjectClip, type ProjectTrack } from "./multitrack-model.ts";

type LegacyProjectTrack = {
  id: number;
  name: string;
  role: TrackRole;
  notes: MidiNote[];
  enabled: boolean;
};

type LegacyPersistedProject = Omit<PersistedProject, "schemaVersion" | "tracks" | "clips"> & {
  schemaVersion: 1;
  tracks: LegacyProjectTrack[];
};

export type PersistedProject = {
  schemaVersion: 2;
  id: "current";
  title: string;
  exportMemo: string;
  exportCounter: number;
  lastModified: string;
  notes: MidiNote[];
  tracks: ProjectTrack[];
  clips: ProjectClip[];
  timing: { ppq: number; timeSignature: MidiTimeSignature };
  settings: {
    bpm: number;
    grid: GridValue;
    timeNudgeGrid: GridValue;
    minimumNoteBeats: number;
    lengthAssist: boolean;
    micSensitivity: number;
    noteDetail: number;
    referenceBars: number;
    barSettings: Record<number, BarProcessingSetting>;
  };
};

export type ProjectStorageKind = "indexeddb" | "localstorage";

const DATABASE_NAME = "p013-multitrack-vocal-midi-projects";
const DATABASE_VERSION = 1;
const STORE_NAME = "projects";
const CURRENT_PROJECT_ID = "current";
const FALLBACK_KEY = "p013.current-project.v2";
const LEGACY_FALLBACK_KEY = "p013.current-project.v1";
const GRIDS = new Set<GridValue>(["1/4", "1/8", "1/16", "1/32"]);
const ROLES = new Set<TrackRole>(["melody", "bass", "strings", "synth"]);
const PLAYBACK_MODES = new Set<PlaybackMode>(["audio", "midi", "both"]);
const MONITOR_SOURCES = new Set<OverdubMonitorSource>(["off", "audio", "midi", "both"]);

function finite(value: unknown, fallback: number, minimum: number, maximum: number): number {
  return typeof value === "number" && Number.isFinite(value)
    ? Math.max(minimum, Math.min(maximum, value))
    : fallback;
}

function finiteOrNull(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function validNotes(value: unknown): MidiNote[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((candidate) => {
    if (!candidate || typeof candidate !== "object") return [];
    const note = candidate as Partial<MidiNote>;
    if (typeof note.midi !== "number" || typeof note.startBeat !== "number" || typeof note.durationBeats !== "number") return [];
    if (![note.midi, note.startBeat, note.durationBeats].every(Number.isFinite)) return [];
    return [{
      ...note,
      midi: Math.max(0, Math.min(127, note.midi)),
      startBeat: note.startBeat,
      durationBeats: Math.max(1 / 960, note.durationBeats),
      velocity: finite(note.velocity, 92, 1, 127),
    } as MidiNote];
  });
}

function commonFields(candidate: Partial<PersistedProject | LegacyPersistedProject>) {
  const settings = candidate.settings ?? {} as PersistedProject["settings"];
  const timing = candidate.timing ?? { ppq: 480, timeSignature: { numerator: 4, denominator: 4 } };
  const timeSignature = timing.timeSignature ?? { numerator: 4, denominator: 4 };
  return {
    id: CURRENT_PROJECT_ID as "current",
    title: typeof candidate.title === "string" && candidate.title.trim() ? candidate.title.slice(0, 64) : "無題の曲",
    exportMemo: typeof candidate.exportMemo === "string" ? candidate.exportMemo.slice(0, 32) : "",
    exportCounter: Math.max(1, Math.round(finite(candidate.exportCounter, 1, 1, 9999))),
    lastModified: typeof candidate.lastModified === "string" ? candidate.lastModified : new Date(0).toISOString(),
    notes: validNotes(candidate.notes),
    timing: {
      ppq: Math.round(finite(timing.ppq, 480, 24, 9600)),
      timeSignature: {
        numerator: Math.round(finite(timeSignature.numerator, 4, 1, 32)),
        denominator: Math.round(finite(timeSignature.denominator, 4, 1, 128)),
      },
    },
    settings: {
      bpm: finite(settings.bpm, 100, 20, 400),
      grid: GRIDS.has(settings.grid) ? settings.grid : "1/8" as GridValue,
      timeNudgeGrid: GRIDS.has(settings.timeNudgeGrid) ? settings.timeNudgeGrid : "1/8" as GridValue,
      minimumNoteBeats: finite(settings.minimumNoteBeats, 0.25, 1 / 32, 8),
      lengthAssist: settings.lengthAssist !== false,
      micSensitivity: finite(settings.micSensitivity, 65, 0, 100),
      noteDetail: finite(settings.noteDetail, 72, 0, 100),
      referenceBars: Math.round(finite(settings.referenceBars, 4, 1, 64)),
      barSettings: settings.barSettings && typeof settings.barSettings === "object" ? settings.barSettings : {},
    },
  };
}

function migrateLegacyProject(candidate: Partial<LegacyPersistedProject>): PersistedProject {
  const common = commonFields(candidate);
  const pairs = Array.isArray(candidate.tracks) ? candidate.tracks.flatMap((legacy, index) => {
    if (!legacy || typeof legacy !== "object") return [];
    const item = legacy as Partial<LegacyProjectTrack>;
    const suffix = Math.max(1, Math.round(finite(item.id, index + 1, 1, Number.MAX_SAFE_INTEGER))).toString();
    return [createTrackWithClip({
      trackId: `track-${suffix}`,
      clipId: `clip-${suffix}`,
      name: typeof item.name === "string" ? item.name.slice(0, 48) : "Track",
      role: ROLES.has(item.role as TrackRole) ? item.role as TrackRole : "melody",
      notes: validNotes(item.notes),
      ppq: common.timing.ppq,
    })];
  }) : [];
  return {
    schemaVersion: 2,
    ...common,
    tracks: pairs.map(({ track }, index) => ({ ...track, muted: candidate.tracks?.[index]?.enabled === false })),
    clips: pairs.map(({ clip }) => clip),
  };
}

function normalizeV2Project(candidate: Partial<PersistedProject>): PersistedProject {
  const common = commonFields(candidate);
  const clips = Array.isArray(candidate.clips) ? candidate.clips.flatMap((value) => {
    if (!value || typeof value !== "object") return [];
    const clip = value as Partial<ProjectClip>;
    if (typeof clip.id !== "string" || typeof clip.trackId !== "string") return [];
    const recording = clip.recordingTiming;
    return [{
      id: clip.id,
      trackId: clip.trackId,
      name: typeof clip.name === "string" ? clip.name.slice(0, 64) : "Clip",
      startTick: Math.round(finite(clip.startTick, 0, 0, Number.MAX_SAFE_INTEGER)),
      durationTicks: Math.round(finite(clip.durationTicks, common.timing.ppq, 1, Number.MAX_SAFE_INTEGER)),
      audio: clip.audio && typeof clip.audio.storageKey === "string" ? {
        storageKey: clip.audio.storageKey,
        mimeType: typeof clip.audio.mimeType === "string" ? clip.audio.mimeType : "application/octet-stream",
        durationMs: finite(clip.audio.durationMs, 0, 0, Number.MAX_SAFE_INTEGER),
      } : null,
      midi: clip.midi ? { notes: validNotes(clip.midi.notes) } : null,
      recordingTiming: recording ? {
        plannedStartAudioTime: finiteOrNull(recording.plannedStartAudioTime),
        observedRecordingStartAudioTime: finiteOrNull(recording.observedRecordingStartAudioTime),
        recordingOffsetMs: finite(recording.recordingOffsetMs, 0, -60000, 60000),
        manualCalibrationMs: finite(recording.manualCalibrationMs, 0, -60000, 60000),
      } : null,
    } satisfies ProjectClip];
  }) : [];
  const clipById = new Map(clips.map((clip) => [clip.id, clip]));
  const tracks = Array.isArray(candidate.tracks) ? candidate.tracks.flatMap((value) => {
    if (!value || typeof value !== "object") return [];
    const track = value as Partial<ProjectTrack>;
    if (typeof track.id !== "string") return [];
    return [{
      id: track.id,
      name: typeof track.name === "string" ? track.name.slice(0, 48) : "Track",
      role: ROLES.has(track.role as TrackRole) ? track.role as TrackRole : "melody",
      clipIds: Array.isArray(track.clipIds) ? track.clipIds.filter((id): id is string => typeof id === "string" && clipById.get(id)?.trackId === track.id) : [],
      playbackMode: PLAYBACK_MODES.has(track.playbackMode as PlaybackMode) ? track.playbackMode as PlaybackMode : "midi",
      overdubMonitorSource: MONITOR_SOURCES.has(track.overdubMonitorSource as OverdubMonitorSource) ? track.overdubMonitorSource as OverdubMonitorSource : "midi",
      muted: track.muted === true,
      solo: track.solo === true,
      gain: finite(track.gain, 1, 0, 2),
      keyAnalysis: track.keyAnalysis && (track.keyAnalysis.mode === "major" || track.keyAnalysis.mode === "minor") ? {
        tonicIndex: Math.round(finite(track.keyAnalysis.tonicIndex, 0, 0, 11)),
        mode: track.keyAnalysis.mode,
        confidence: finite(track.keyAnalysis.confidence, 0, 0, 1),
        analyzedAt: typeof track.keyAnalysis.analyzedAt === "string" ? track.keyAnalysis.analyzedAt : new Date(0).toISOString(),
      } : null,
    } satisfies ProjectTrack];
  }) : [];
  const trackIds = new Set(tracks.map((track) => track.id));
  return { schemaVersion: 2, ...common, tracks, clips: clips.filter((clip) => trackIds.has(clip.trackId)) };
}

export function normalizePersistedProject(value: unknown): PersistedProject | null {
  if (!value || typeof value !== "object") return null;
  const candidate = value as Partial<PersistedProject | LegacyPersistedProject>;
  if (candidate.id !== CURRENT_PROJECT_ID) return null;
  if (candidate.schemaVersion === 1) return migrateLegacyProject(candidate as Partial<LegacyPersistedProject>);
  if (candidate.schemaVersion === 2) return normalizeV2Project(candidate as Partial<PersistedProject>);
  return null;
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(STORE_NAME)) database.createObjectStore(STORE_NAME, { keyPath: "id" });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IndexedDBを開けませんでした"));
  });
}

async function loadFromIndexedDb(): Promise<PersistedProject | null> {
  const database = await openDatabase();
  try {
    return await new Promise((resolve, reject) => {
      const request = database.transaction(STORE_NAME, "readonly").objectStore(STORE_NAME).get(CURRENT_PROJECT_ID);
      request.onsuccess = () => resolve(normalizePersistedProject(request.result));
      request.onerror = () => reject(request.error ?? new Error("Projectを読み込めませんでした"));
    });
  } finally {
    database.close();
  }
}

async function saveToIndexedDb(project: PersistedProject): Promise<void> {
  const database = await openDatabase();
  try {
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction(STORE_NAME, "readwrite");
      transaction.objectStore(STORE_NAME).put(project);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error ?? new Error("Projectを保存できませんでした"));
      transaction.onabort = () => reject(transaction.error ?? new Error("Project保存が中断されました"));
    });
  } finally {
    database.close();
  }
}

export async function loadCurrentProject(): Promise<{ project: PersistedProject | null; storage: ProjectStorageKind }> {
  if (typeof window === "undefined") return { project: null, storage: "localstorage" };
  if ("indexedDB" in window) {
    try {
      return { project: await loadFromIndexedDb(), storage: "indexeddb" };
    } catch (caught) {
      console.warn("[P-013 project] IndexedDB load failed; using localStorage fallback", caught);
    }
  }
  const stored = window.localStorage.getItem(FALLBACK_KEY) ?? window.localStorage.getItem(LEGACY_FALLBACK_KEY);
  try {
    return { project: stored ? normalizePersistedProject(JSON.parse(stored)) : null, storage: "localstorage" };
  } catch {
    window.localStorage.removeItem(FALLBACK_KEY);
    return { project: null, storage: "localstorage" };
  }
}

export async function saveCurrentProject(project: PersistedProject): Promise<ProjectStorageKind> {
  if ("indexedDB" in window) {
    try {
      await saveToIndexedDb(project);
      return "indexeddb";
    } catch (caught) {
      console.warn("[P-013 project] IndexedDB save failed; using localStorage fallback", caught);
    }
  }
  window.localStorage.setItem(FALLBACK_KEY, JSON.stringify(project));
  return "localstorage";
}
